/**
 * Quick find (N4) — the customer's documents, loaded on first search and kept
 * for 5 minutes. Uses the same BC-backed service calls as the dashboard; any
 * source that fails is skipped (search still works on the rest).
 */
import { toIndexedDocument, type IndexedDocument } from './quickFindModel';

type Rec = Record<string, unknown>;
interface DocumentSources {
  getOrders(customerId: string, options?: { limit?: number }): Promise<{ openOrders: Rec[]; postedOrders: Rec[] }>;
  getQuotes?(): Promise<{ success: boolean; data?: Rec[] }>;
  getCreditMemos?(): Promise<{ success: boolean; data?: Rec[] }>;
  getPostedShipments?(): Promise<{ success: boolean; data?: Rec[] }>;
}

const TTL_MS = 5 * 60 * 1000;
const cache = new Map<string, { at: number; docs: IndexedDocument[] }>();
const inflight = new Map<string, Promise<IndexedDocument[]>>();

export function clearDocumentIndex(): void {
  cache.clear();
  inflight.clear();
}

export async function loadDocumentIndex(
  customerId: string,
  opts: { quotesEnabled?: boolean; source?: DocumentSources; now?: () => number } = {},
): Promise<IndexedDocument[]> {
  const now = opts.now ?? Date.now;
  const key = `${customerId}|${opts.quotesEnabled ? 'q' : ''}`;
  const hit = cache.get(key);
  if (hit && now() - hit.at < TTL_MS) {return hit.docs;}
  const running = inflight.get(key);
  if (running) {return running;}

  const p = (async () => {
    const source = opts.source ?? ((await import('../../services/DataServiceFactory')).dataService as unknown as DocumentSources);
    const docs: IndexedDocument[] = [];
    const add = (kind: Parameters<typeof toIndexedDocument>[0], rows: Rec[] | undefined) => {
      for (const r of rows ?? []) { const d = toIndexedDocument(kind, r); if (d) {docs.push(d);} }
    };
    const creditMemosOff = (() => { try { return sessionStorage.getItem('bc_credit_memos_disabled') === 'true'; } catch { return false; } })();
    await Promise.allSettled([
      source.getOrders(customerId, { limit: 200 }).then(r => { add('order', r.openOrders); add('invoice', r.postedOrders); }),
      opts.quotesEnabled && source.getQuotes ? source.getQuotes().then(r => add('quote', r.data)) : Promise.resolve(),
      !creditMemosOff && source.getCreditMemos ? source.getCreditMemos().then(r => add('credit-memo', r.data)) : Promise.resolve(),
      source.getPostedShipments ? source.getPostedShipments().then(r => add('shipment', r.data)) : Promise.resolve(),
    ]);
    cache.set(key, { at: now(), docs });
    return docs;
  })().finally(() => inflight.delete(key));
  inflight.set(key, p);
  return p;
}
